import { DASHBOARD_ASSISTANT_RUNTIME_MODE } from '../constants';
import { createToolMessageId } from './helpers';

const MAX_ARRAY_ITEMS = 20;
const MAX_OBJECT_KEYS = 50;
const MAX_STRING_LENGTH = 1000;
const MAX_DEPTH = 5;
const SENSITIVE_KEY_PATTERN = /(authorization|cookie|password|secret|session|token|api[_-]?key)/i;

export function createFrontendToolActionMessage({
  actionType = 'tool',
  content,
  displayPayload,
  status,
  toolArgs,
  toolName,
  toolResult,
}) {
  const sanitizedDisplayPayload = displayPayload ? sanitizeDisplayPayload(displayPayload) : undefined;
  const summary =
    content ||
    sanitizedDisplayPayload?.summary ||
    (toolName ? `Used ${toolName}` : 'Used a dashboard tool');

  return {
    id: createToolMessageId(),
    actionType,
    content: summary,
    displayPayload: sanitizedDisplayPayload,
    kind: 'action',
    metadata: {
      actionType,
      runtimeMode: DASHBOARD_ASSISTANT_RUNTIME_MODE,
      source: 'dashboard-assistant-frontend-tool',
      toolArgs: sanitizeToolPayload(toolArgs),
      toolName,
      toolResult: sanitizeToolPayload(toolResult),
      toolStatus: status || toolResult?.status,
    },
    role: 'assistant',
    timestamp: new Date().toISOString(),
  };
}

function sanitizeDisplayPayload(displayPayload) {
  const sanitized = sanitizeToolPayload(displayPayload);
  if (!sanitized || typeof sanitized !== 'object') {
    return undefined;
  }

  return {
    renderer: truncateString(String(sanitized.renderer || 'dashboard_tool')),
    summary:
      typeof sanitized.summary === 'string'
        ? truncateString(sanitized.summary, 500)
        : undefined,
    data:
      sanitized.data && typeof sanitized.data === 'object'
        ? sanitized.data
        : {},
  };
}

export function createFrontendNavigationToolMessage({
  label,
  path,
  status = 'opened',
  toolArgs,
  toolName,
  toolResult,
}) {
  const fallbackLabel = label || path || 'dashboard page';
  const displayPayload = {
    renderer: 'dashboard_navigation',
    summary: `${status === 'opened' ? 'Opened' : 'Suggested'} ${fallbackLabel}`,
    data: {
      label: fallbackLabel,
      path,
      status,
    },
  };

  return createFrontendToolActionMessage({
    actionType: 'navigation',
    content: displayPayload.summary,
    displayPayload,
    status,
    toolArgs,
    toolName,
    toolResult: toolResult || { path, status },
  });
}

export function createFrontendToolErrorMessage({
  message,
  status = 'error',
  toolArgs,
  toolName,
  toolResult,
}) {
  return createFrontendToolActionMessage({
    actionType: 'error',
    content: message || 'Dashboard tool failed.',
    status,
    toolArgs,
    toolName,
    toolResult: toolResult || { message, status },
  });
}

function sanitizeToolPayload(value, depth = 0) {
  if (value === undefined) {
    return undefined;
  }
  if (value === null || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    return truncateString(value);
  }
  if (depth >= MAX_DEPTH) {
    return '[Truncated]';
  }
  if (Array.isArray(value)) {
    const next = value
      .slice(0, MAX_ARRAY_ITEMS)
      .map((item) => sanitizeToolPayload(item, depth + 1));
    if (value.length > MAX_ARRAY_ITEMS) {
      next.push({ truncatedCount: value.length - MAX_ARRAY_ITEMS });
    }
    return next;
  }
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .slice(0, MAX_OBJECT_KEYS)
        .map(([key, item]) => [
          key,
          SENSITIVE_KEY_PATTERN.test(key)
            ? '[Redacted]'
            : sanitizeToolPayload(item, depth + 1),
        ]),
    );
  }

  return String(value);
}

function truncateString(value, maxLength = MAX_STRING_LENGTH) {
  return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
}
